import { v4 } from 'uuid';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import { processApiRecipients } from 'utils/recipientSearch';

import { call } from '../xmAPI';
import {
  RESET_RECIPIENT_SEARCH_RESULTS,
  MINIMUM_SEARCH_TERM,
  SEARCH_RECIPIENT_START,
  SEARCH_RECIPIENT_BATCH_SIZE,
  SEARCH_RECIPIENT_BATCH_LIMIT,
  SEARCH_RECIPIENT_SUCCESS,
  SEARCH_RECIPIENT_FAIL,
  SEARCH_PEOPLE_START,
  SEARCH_PEOPLE_SUCCESS,
  SEARCH_PEOPLE_FAIL,
  RESET_RECIPIENT_SEARCH_STATE,
  SEARCH_RECIPIENT_SET_SELECTED_RECIPIENT_LIST,
  SEARCH_RECIPIENT_SET_EXCLUDED_RECIPIENT_LIST,
  INITIALIZE,
  SEARCH_RECIPIENT_GET_ALL,
  SEARCH_RECIPIENT_EXPAND_RECIPIENT,
  SEARCH_RECIPIENT_COUNT_PEOPLE,
  SEARCH_GROUPS_START,
  SEARCH_GROUPS_SUCCESS,
  SEARCH_GROUPS_FAIL,
} from './constant';

const XM_API_MAX_LIMIT = 1000;
const REMOTE_BASE_URL = '/webapi/xm/1/recipients';
const REMOTE_BASE_URL_PEOPLE = '/webapi/xm/1/people';
const REMOTE_BASE_URL_PEOPLE_EBFD = '/webapi/xm/1/ebfd/users';
const GROUPS_URL = '/webapi/xm/1/groups';
let latestSearchQueryId;

const GENERIC_SEARCH = 'generic';

// 'searchName' param for EverbridgeFlowDesigner API and 'search' param for xmatters
const getSearchParamName = (shouldSendToEbfdEndpoint, search) => {
  return shouldSendToEbfdEndpoint ? { searchName: search } : { search };
};

export const setSelectedRecipient = recipient => () => {
  if (window && window.XM_setSelectedRecipient) {
    window.XM_setSelectedRecipient(recipient);
  }
  return Promise.resolve();
};

export const setSelectedRecipientType = recipientType => () => {
  if (window && window.XM_setSelectedRecipientType) {
    window.XM_setSelectedRecipientType(recipientType);
  }
  return Promise.resolve();
};

export const setSelectedSearchType = searchType => () => {
  if (window && window.XM_setSelectedSearchType) {
    window.XM_setSelectedSearchType(searchType);
  }
  return Promise.resolve();
};

export const resetSearchResults = (page, permissionSearchMode) => dispatch => {
  dispatch({
    type: RESET_RECIPIENT_SEARCH_RESULTS,
    payload: {
      data: [],
      results: [],
      total: 0,
      permissionSearchMode,
    },
    meta: { page },
  });
};

export const doSearchRecipient = opts => dispatch => {
  if (!opts) {
    return Promise.resolve();
  }

  const {
    embed,
    excludedRecipients = [],
    // xm-api doesn't support an "all user" type of recipient, so when we want that as
    // an option when searching for a recipient, we need to send a signal to the reducers to fake one.
    permissionSearchMode = false,
    page,
    recipientType,
    search,
    searchType,
    startIndex = 0,
    stopIndex,
    shouldSendToEbfdEndpoint,
    include,
    intl = null,
  } = opts;

  const searchQueryId = v4();
  latestSearchQueryId = searchQueryId;
  if (!recipientType || !searchType) {
    return Promise.resolve();
  }

  dispatch({
    type: SEARCH_RECIPIENT_START,
    meta: { page },
  });

  const calculatedStopIndex =
    stopIndex || startIndex + SEARCH_RECIPIENT_BATCH_SIZE;
  const calculatedLimit = calculatedStopIndex - startIndex;
  const limit = Math.min(
    // Ensure no fewer than (some constant):
    Math.max(calculatedLimit, SEARCH_RECIPIENT_BATCH_LIMIT),
    // Ensure no more than xm-api's maximum:
    XM_API_MAX_LIMIT,
  );

  const params = {
    limit,
    searchType,
    ...getSearchParamName(shouldSendToEbfdEndpoint, search),
    offset: startIndex,
    sort: 'displayName',
  };

  if (embed) {
    params.embed = embed;
  }

  if (include) {
    params.include = include;
  }

  const paramString = [
    Object.keys(params)
      .map(key => `${key}=${encodeURIComponent(params[key])}`)
      .join('&'),
    recipientType.map(r => `recipientType=${r}`).join('&'),
  ].join('&');

  const remoteBaseUrl = shouldSendToEbfdEndpoint
    ? REMOTE_BASE_URL_PEOPLE_EBFD
    : REMOTE_BASE_URL;

  return call(`${remoteBaseUrl}?${paramString}`)
    .then(json => {
      if (latestSearchQueryId !== searchQueryId) {
        return undefined;
      }

      const initialDataLength = json.data.length;
      const filteredData = json.data.filter(
        item => excludedRecipients.indexOf(item.id) === -1,
      );

      const massagedJson = {
        ...json,
        data: processApiRecipients({ filteredData, shouldSendToEbfdEndpoint }),
      };

      const countFilteredRecipients =
        initialDataLength - massagedJson.data.length;
      massagedJson.count = json.count - countFilteredRecipients;
      massagedJson.total = json.total - countFilteredRecipients;

      dispatch({
        type: SEARCH_RECIPIENT_SUCCESS,
        payload: {
          ...massagedJson,
          permissionSearchMode,
          recipientType,
          search,
          searchType,
          startIndex,
          stopIndex: calculatedStopIndex,
          intl,
        },
        meta: { page },
      });
      return massagedJson;
    })
    .catch(err => {
      dispatch({
        type: SEARCH_RECIPIENT_FAIL,
        payload: err,
        error: true,
        meta: { page },
      });
      throw err;
    });
};

const getRecipientTypeParam = recipientType => {
  if (recipientType === 'ALL') {
    return [];
  }
  return isArray(recipientType) ? recipientType : [recipientType];
};

export const searchRecipient = opts => dispatch => {
  if (!opts) {
    return Promise.resolve();
  }
  const recipientType = getRecipientTypeParam(opts.recipientType);
  return doSearchRecipient({ ...opts, recipientType })(dispatch);
};

export const searchHelper = opts => dispatch => {
  if (!opts) {
    return Promise.resolve();
  }

  const {
    embed,
    excludedRecipients = [],
    fields,
    page,
    search,
    startIndex = 0,
    stopIndex,
    searchStartAction,
    searchSuccessAction,
    searchFailAction,
    searchUrl,
    searchParams,
    operand,
    shouldSendToEbfdEndpoint,
  } = opts;
  const searchQueryId = v4();
  latestSearchQueryId = searchQueryId;

  if (
    !opts.allowBlankSearch &&
    (!search || search.length < MINIMUM_SEARCH_TERM)
  ) {
    return Promise.resolve();
  }

  dispatch({
    type: searchStartAction,
    meta: { page },
  });

  const calculatedStopIndex =
    stopIndex || startIndex + SEARCH_RECIPIENT_BATCH_SIZE;
  const limit = SEARCH_RECIPIENT_BATCH_LIMIT;

  let params = {
    limit,
    offset: startIndex,
    sort: 'displayName',
    ...searchParams,
  };
  if (embed) {
    params.embed = embed;
  }

  if (search?.trim().length > 1) {
    params = {
      ...params,
      ...getSearchParamName(shouldSendToEbfdEndpoint, search),
      fields,
      operand,
    };
  }

  const paramString = Object.keys(params)
    .map(key => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');

  return call(`${searchUrl}?${paramString}`)
    .then(json => {
      if (latestSearchQueryId !== searchQueryId) {
        return undefined;
      }

      const massagedJson = { ...json };
      const initialDataLength = json.data.length;

      const filteredData = json.data.filter(
        item => excludedRecipients.indexOf(item.id) === -1,
      );

      massagedJson.data = processApiRecipients({
        filteredData,
        shouldSendToEbfdEndpoint,
      });

      const countFilteredRecipients =
        initialDataLength - massagedJson.data.length;
      massagedJson.count = json.count - countFilteredRecipients;
      massagedJson.total = json.total - countFilteredRecipients;

      dispatch({
        type: searchSuccessAction,
        payload: {
          ...massagedJson,
          search,
          startIndex,
          stopIndex: calculatedStopIndex,
        },
        meta: { page },
      });
      return massagedJson;
    })
    .catch(err => {
      dispatch({
        type: searchFailAction,
        payload: err,
        error: true,
        meta: { page },
      });
      throw err;
    });
};

export const searchGroups = opts => {
  const searchGroupOpts = {
    ...opts,
    searchStartAction: SEARCH_GROUPS_START,
    searchSuccessAction: SEARCH_GROUPS_SUCCESS,
    searchFailAction: SEARCH_GROUPS_FAIL,
    searchUrl: GROUPS_URL,
    search: opts.search,
    operand: 'AND',
    fields: ['NAME', 'DESCRIPTION'],
    searchParams: {
      ...opts.groupSearchParams,
    },
  };
  return searchHelper(searchGroupOpts);
};

export const searchPeople = opts => {
  const SearchUserOpts = {
    ...opts,
    searchStartAction: SEARCH_PEOPLE_START,
    searchSuccessAction: SEARCH_PEOPLE_SUCCESS,
    searchFailAction: SEARCH_PEOPLE_FAIL,
    searchUrl: opts.shouldSendToEbfdEndpoint
      ? REMOTE_BASE_URL_PEOPLE_EBFD
      : REMOTE_BASE_URL_PEOPLE,
    search: opts.search,
    operand: 'AND',
    fields: ['FIRST_NAME', 'LAST_NAME', 'TARGET_NAME'],
    searchParams: {
      permissions: opts.permissionTypes,
      sort: 'displayName',
    },
  };
  return searchHelper(SearchUserOpts);
};

export const resetRecipientSearchState = page => ({
  type: RESET_RECIPIENT_SEARCH_STATE,
  meta: { page },
});

export const setSelectedRecipientList = (recipients, page) => ({
  type: SEARCH_RECIPIENT_SET_SELECTED_RECIPIENT_LIST,
  payload: recipients,
  meta: { page },
});

export const setExcludedRecipients = (recipients, page = GENERIC_SEARCH) => ({
  type: SEARCH_RECIPIENT_SET_EXCLUDED_RECIPIENT_LIST,
  payload: recipients,
  meta: { page },
});

export const initializeRecipientSearch = page => ({
  type: INITIALIZE,
  meta: { page },
});

export const searchRecipientsGetAll = ({
  page,
  recipientType,
  search,
  searchType,
  include,
}) => ({
  type: SEARCH_RECIPIENT_GET_ALL,
  meta: {
    page,
    xmApi: true,
    uri: REMOTE_BASE_URL,
    method: 'getall',
    params: {
      recipientType: getRecipientTypeParam(recipientType),
      search,
      searchType,
      sort: 'displayName',
      limit: 1000,
      include,
    },
  },
  payload: { page },
});

const getExpandRecipientUrl = ({ id, recipientType }) => {
  if (recipientType === 'DYNAMIC_TEAM') {
    return `/webapi/xm/1/dynamic-teams/${id}/members`;
  }
  return `/webapi/xm/1/groups/${id}/recipients`;
};

export const expandRecipient = ({ recipient }) => ({
  type: SEARCH_RECIPIENT_EXPAND_RECIPIENT,
  meta: {
    xmApi: true,
    uri: getExpandRecipientUrl(recipient),
    method: 'getall',
    params: {
      limit: 1000,
    },
  },
});

export const countUniquePeople = value => ({
  type: SEARCH_RECIPIENT_COUNT_PEOPLE,
  meta: {
    xmApi: true,
    uri: '/webapi/xm/1/recipients/resolved-users-count',
    method: 'POST',
    params: {
      recipients: map(value, 'id'),
    },
  },
  payload: { requestTime: new Date() },
});
